import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, Plus, ChevronDown, ThumbsUp } from 'lucide-react';
import { Button } from './ui/button';
const MovieCard = ({ movie, index, onMovieClick, isModalOpen = false, labelWatermark }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const cardRef = useRef(null);

  const handleMouseEnter = () => {
    // Ne pas permettre le hover si un modal est ouvert
    if (isModalOpen) return;
    
    setIsHovered(true);
    
    // Capturer la position de la carte originale
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setCardPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Écouter les événements de scroll pour fermer la carte hover
  useEffect(() => {
    const handleScroll = () => {
      if (isHovered) {
        setIsHovered(false);
      }
    };

    // Ajouter l'écouteur sur la fenêtre et le document
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [isHovered]);

  const handleHoverCardScroll = (e) => {
    // Empêcher la propagation du scroll vers le parent
    e.stopPropagation();
  };

  // Helper function to get the correct image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    return `images/${imagePath}`;
  };
  const imageUrl = getImageUrl(movie.backdrop_path || movie.poster_path);

  // Créer la carte zoomée en portail
  const ZoomedCard = () => {
    if (!isHovered || isModalOpen) return null;

    return createPortal(
      <div 
        className="fixed pointer-events-none transition-all duration-500 ease-out"
        style={{
          top: cardPosition.top,
          left: cardPosition.left,
          width: cardPosition.width,
          height: cardPosition.height,
          transform: 'scale(1.36)',
          transformOrigin: 'center center',
          zIndex: 10000,
          opacity: 1,
        }}
      >
        <div className="w-64 h-36 rounded-lg overflow-hidden shadow-2xl border-2 border-white/30 transition-all duration-500 ease-out">
          <img
            src={getImageUrl(movie.backdrop_path || movie.poster_path)}
            alt={movie.title || movie.name}
            className="w-full h-full object-cover transition-transform duration-500 ease-out"
          />
        </div>
      </div>,
      document.body
    );
  };

  const handleCardClick = (e) => {
    // Don't trigger modal if clicking on buttons in hover card
    if (e.target.closest('button')) return;
    onMovieClick(movie);
  };

  const handlePlayClick = (e) => {
    e.stopPropagation();
    // For play button, we want to open the modal which will allow going fullscreen
    onMovieClick(movie);
  };

  const handleChevronClick = (e) => {
    e.stopPropagation();
    // ChevronDown should also open the modal for more info
    onMovieClick(movie);
  };

  return (
    <>
      <div 
        ref={cardRef}
        className="relative flex-shrink-0 cursor-pointer group transition-all duration-500 ease-out"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleCardClick}
        style={{
          opacity: isHovered ? 0.3 : 1,
          transform: isHovered ? 'scale(0.95)' : 'scale(1)',
          zIndex: 1,
        }}
      >
        {/* Movie Poster */}
        <div className="w-64 h-36 rounded-lg overflow-hidden shadow-lg transition-all duration-500 ease-out relative">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={movie.title || movie.name}
              className="w-full h-full object-cover transition-all duration-500 ease-out"
            />
          ) : null}
          {/* Inline Title Overlay */}
          <div className="absolute bottom-0 left-0 w-full bg-black/90 text-gray-300 text-base px-3 py-2 truncate" style={{zIndex: 2}}>
            {movie.title || movie.name}
          </div>
          {/* Label Watermark (filigrane) - now top left */}
          {labelWatermark && (
            <div className="absolute top-2 left-2 px-3 py-1 bg-black/80 text-white text-xs font-bold rounded-lg pointer-events-none select-none" style={{zIndex: 3, opacity: 0.95}}>
              {labelWatermark}
            </div>
          )}
          {/* Publication Date */}
          {labelWatermark && movie.release_date && (
            <div className="absolute top-2 right-2 px-3 py-1 bg-black/80 text-white text-xs font-bold rounded-lg pointer-events-none select-none" style={{zIndex: 3, opacity: 0.95}}>
              {new Date(movie.release_date).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit'
              })}
            </div>
          )}
        </div>
      </div>

      {/* Carte zoomée rendue en portail */}
      <ZoomedCard />
    </>
  );
};

export default MovieCard;